import React, { createContext, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import type { AnalyticsReportingMode, Genie2ReportingMode } from '~/utils/analyticsReportingMode';
import {
  ANALYTICS_REPORTING_MODE_SEARCH_PARAM,
  isGenie2ReportingMode,
  parseGenie2ReportingModeSearchParam,
  staffConfigReportingModeQuery,
  usesAnalyticsStatsUi,
} from '~/utils/analyticsReportingMode';

export type StaffAnalyticsConfigModeValue = {
  /** Persisted flag from access GET (null while loading). */
  persistedMode: AnalyticsReportingMode | null;
  /** Valid pre-seed search param, or null if chooser not completed / live account. */
  selectedConfigMode: Genie2ReportingMode | null;
  /**
   * Query to send on staff config routes, or undefined when live / chooser incomplete.
   * When chooser incomplete and legacy, callers must not fetch catalog/assign.
   */
  configQuery: { analyticsReportingMode: Genie2ReportingMode } | undefined;
  /**
   * Genie 2.0 mode for write bodies: live persisted mode, or pre-seed chooser selection.
   * Null while Classic Genie has no chooser selection yet.
   */
  writeAnalyticsReportingMode: Genie2ReportingMode | null;
  /** True while persisted is legacy and search param is not a valid Genie2 mode. */
  needsUniverseChooser: boolean;
  /** True once we know persisted mode and either live or chooser completed. */
  canLoadConfigViews: boolean;
  isPersistedLive: boolean;
};

const StaffAnalyticsConfigModeContext = createContext<StaffAnalyticsConfigModeValue | null>(null);

export function StaffAnalyticsConfigModeProvider({
  persistedMode,
  children,
}: {
  persistedMode: AnalyticsReportingMode | null;
  children: React.ReactNode;
}) {
  const [searchParams] = useSearchParams();
  const value = useMemo((): StaffAnalyticsConfigModeValue => {
    const selectedFromSearch = parseGenie2ReportingModeSearchParam(
      searchParams.get(ANALYTICS_REPORTING_MODE_SEARCH_PARAM),
    );
    const isPersistedLive = persistedMode != null && usesAnalyticsStatsUi(persistedMode);
    const needsUniverseChooser = persistedMode === 'legacy' && selectedFromSearch == null;
    const canLoadConfigViews =
      persistedMode != null && (isPersistedLive || selectedFromSearch != null);
    const configQuery =
      persistedMode === 'legacy' && selectedFromSearch != null
        ? staffConfigReportingModeQuery(persistedMode, selectedFromSearch)
        : undefined;
    const writeAnalyticsReportingMode = isGenie2ReportingMode(persistedMode)
      ? persistedMode
      : selectedFromSearch;

    return {
      persistedMode,
      selectedConfigMode: isPersistedLive ? null : selectedFromSearch,
      configQuery,
      writeAnalyticsReportingMode,
      needsUniverseChooser,
      canLoadConfigViews,
      isPersistedLive,
    };
  }, [persistedMode, searchParams]);

  return (
    <StaffAnalyticsConfigModeContext.Provider value={value}>
      {children}
    </StaffAnalyticsConfigModeContext.Provider>
  );
}

export function useStaffAnalyticsConfigMode(): StaffAnalyticsConfigModeValue {
  const ctx = useContext(StaffAnalyticsConfigModeContext);
  if (!ctx) {
    // No provider (e.g. mid-migration): allow reads; writes stay gated by callers.
    return {
      persistedMode: null,
      selectedConfigMode: null,
      configQuery: undefined,
      writeAnalyticsReportingMode: null,
      needsUniverseChooser: false,
      canLoadConfigViews: true,
      isPersistedLive: false,
    };
  }
  return ctx;
}
